import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Copy, Phone, MessageSquareIcon, File, ChevronDown } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState, useEffect } from "react";
import { useContactActions } from "@/features/contact-actions/hooks/use-contact-actions";
import { getUserNotes, Note } from "../services/get-user-notes";
import { getUserBadgeText } from "@/helpers/get-user-badge-text";

interface UserTableProps {
  users: any[];
  onUserClick: (userId: string) => void;
}

interface NoteButtonProps {
  userId: string;
}

const NoteButton: React.FC<NoteButtonProps> = ({ userId }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [closeTimeout, setCloseTimeout] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen && notes.length === 0) {
      setIsLoading(true);
      getUserNotes(userId)
        .then((fetchedNotes) => {
          setNotes(fetchedNotes);
        })
        .catch((error) => {
          console.error("Failed to fetch notes:", error);
          toast({
            title: "Error",
            description: "Failed to load notes",
            variant: "destructive",
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen, userId, notes.length]);

  const handleMouseEnter = () => {
    if (closeTimeout) {
      clearTimeout(closeTimeout);
      setCloseTimeout(null);
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    const timeout = setTimeout(() => {
      setIsOpen(false);
    }, 200); // Small delay to allow moving to popover content
    setCloseTimeout(timeout);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate(); // No leading zero
    const month = date.toLocaleDateString("en-GB", { month: "short" }).toUpperCase();
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className="text-xs text-orange-500 hover:text-orange-600 underline decoration-orange-500 hover:decoration-orange-600 text-left"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          NOTES
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 max-h-96 overflow-y-auto bg-black text-white border-black rounded-lg p-4"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        side="right"
        align="start"
      >
        {isLoading ? (
          <div className="text-sm text-white">Loading notes...</div>
        ) : notes.length === 0 ? (
          <div className="text-sm text-white">No notes available</div>
        ) : (
          <div className="space-y-4">
            {notes.map((note) => (
              <div key={note.id} className="space-y-2">
                <div className="text-orange-500 text-sm font-semibold underline decoration-orange-500">
                  NOTE
                </div>
                <p className="text-sm text-white italic whitespace-pre-wrap">{note.content}</p>
                <p className="text-xs text-white not-italic">
                  UPDATED ON {formatDate(note.updated_at)}
                </p>
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const UserTable: React.FC<UserTableProps> = ({ users, onUserClick }) => {
  const { callViaExotel, openWatiChat, calling, hasExotelPhone } = useContactActions();

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied", description: `${label} copied to clipboard` });
    } catch {
      toast({
        title: "Error",
        description: `Failed to copy ${label}`,
        variant: "destructive",
      });
    }
  };

  if (users.length === 0) {
    return null;
  }

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse min-w-[1200px]">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Name</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Type</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Interested In</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Login</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Email</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Phone No.</th>
            <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Quick Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const {
              id,
              full_name,
              is_lead,
              otpless_identifier,
              emails,
              phone_numbers,
              latest_note,
              country,
              product_interests,
              document_ids,
            } = user;
            const interests: string[] = product_interests ?? [];

            const primaryEmail = emails?.[0] || "";
            const primaryPhone = phone_numbers?.[0] || otpless_identifier || "";
            // Contact actions (WhatsApp / Exotel) target the login number the user
            // verified with (otpless_identifier, shown in the "Login" column), since
            // that's the number their WhatsApp chat lives under. Fall back to a listed
            // phone number, and skip otpless_identifier when it's an email login.
            const loginNumber =
              otpless_identifier && !otpless_identifier.includes("@")
                ? otpless_identifier
                : "";
            const contactNumber = loginNumber || phone_numbers?.[0] || "";

            return (
              <tr key={id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="py-3 px-4">
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => onUserClick(id)}
                      className="text-sm font-medium text-gray-900 hover:text-orange-500 hover:underline text-left"
                    >
                      {full_name || "No Name"}
                    </button>
                    {latest_note && <NoteButton userId={id} />}
                  </div>
                </td>
                <td className="py-3 px-4">
                  {is_lead ? (
                    <Badge
                      className="bg-purple-100 text-purple-700 border-0 rounded-full px-2.5 py-0.5 text-xs font-semibold inline-flex items-center"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-500 mr-1.5 inline-block" />
                      {getUserBadgeText(country, is_lead)}
                    </Badge>
                  ) : (
                    <Badge
                      className="bg-green-100 text-green-700 border-0 rounded-full px-2.5 py-0.5 text-xs font-semibold inline-flex items-center"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 mr-1.5 inline-block" />
                      {getUserBadgeText(country, is_lead)}
                    </Badge>
                  )}
                </td>
                <td className="py-3 px-4 text-sm text-gray-700">
                  {interests.length ? (
                    <span
                      className="inline-flex items-center gap-1.5"
                      title={interests.join(", ")}
                    >
                      <span>{interests[0]}</span>
                      {interests.length > 1 && (
                        <span className="bg-gray-100 text-gray-600 rounded-full px-1.5 py-0.5 text-xs font-medium">
                          +{interests.length - 1}
                        </span>
                      )}
                    </span>
                  ) : (
                    "—"
                  )}
                </td>
                <td className="py-3 px-4 text-sm text-gray-700">
                  {otpless_identifier || "—"}
                </td>
                <td className="py-3 px-4 text-sm text-gray-700">
                  {primaryEmail || "—"}
                </td>
                <td className="py-3 px-4 text-sm text-gray-700">
                  {primaryPhone || "—"}
                </td>
                <td className="py-3 px-4">
                  <div className="flex gap-2 w-[260px]">
                    <div className="flex flex-col gap-2 flex-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="bg-gray-100 text-gray-700 hover:bg-gray-200 h-8 text-xs px-2.5 rounded-md"
                        onClick={() => openWatiChat(contactNumber)}
                        disabled={!contactNumber}
                      >
                        <MessageSquareIcon className="h-3 w-3 mr-1.5" />
                        Whatsapp
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="bg-gray-100 text-gray-700 hover:bg-gray-200 h-8 text-xs px-2.5 rounded-md"
                        onClick={() => callViaExotel(contactNumber)}
                        disabled={!contactNumber || calling || !hasExotelPhone}
                        title={!hasExotelPhone ? "Set your ExoTel number in Admin Panel → Internal Users" : undefined}
                      >
                        <Phone className="h-3 w-3 mr-1.5" />
                        {calling ? "Calling…" : "Call"}
                      </Button>
                    </div>
                    <div className="flex flex-col gap-2 flex-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="bg-gray-100 text-gray-700 hover:bg-gray-200 h-8 text-xs px-2.5 rounded-md"
                        onClick={() => copyToClipboard(id, "User ID")}
                      >
                        <Copy className="h-3 w-3 mr-1.5" />
                        Copy User ID
                      </Button>
                      {document_ids && Object.keys(document_ids).length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="secondary"
                              size="sm"
                              className="bg-gray-100 text-gray-700 hover:bg-gray-200 h-8 text-xs px-2.5 rounded-md"
                            >
                              <File className="h-3 w-3 mr-1.5" />
                              Documents
                              <ChevronDown className="h-3 w-3 ml-1.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {Object.entries(document_ids).map(([topic, docId]) => (
                              <DropdownMenuItem
                                key={topic}
                                onClick={() => {
                                  const docUrl = `https://docs.google.com/document/d/${docId}`;
                                  window.open(docUrl, '_blank');
                                }}
                                className="cursor-pointer"
                              >
                                <File className="h-4 w-4 mr-2" />
                                <span className="flex-1 capitalize">{topic}</span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 ml-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const docUrl = `https://docs.google.com/document/d/${docId}`;
                                    copyToClipboard(docUrl, `${topic} document`);
                                  }}
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default UserTable;

